import { useState, useEffect, useRef, useCallback, CSSProperties, createElement, FC } from "react";

import { SVGInjector } from "@tanem/svg-injector";

const svgNamespace = "http://www.w3.org/2000/svg";
const xlinkNamespace = "http://www.w3.org/1999/xlink";
const WrapperClassName = "svg-wrapper";

const ownerWindow = (node?: Node | null) => {
  const doc = node?.ownerDocument || document;
  return doc.defaultView || window;
};

type WrapperElementTag = "div" | "span" | "svg";
type HTMLWrapperType = HTMLSpanElement | HTMLDivElement;
type SVGWrapperType = SVGSVGElement;
export type WrapperType = HTMLWrapperType | SVGWrapperType;

interface BaseProps {
  /** The source URL or path to the SVG file. */
  src: string;

  /** Component to render while the SVG is loading. */
  loading?: React.ElementType;

  /** Callback function when an error occurs during injection. */
  onError?: (error: unknown) => void;

  /** Title to add as a `<title>` element inside the SVG for accessibility. */
  title?: string;

  /** A description to add as a `<desc>` element inside the SVG for accessibility. */
  desc?: string;

  /** CSS styles to apply directly to the injected SVG element. */
  svgStyle?: CSSProperties;

  /** The type of wrapper to use around the SVG (default is `div`). */
  wrapper?: WrapperElementTag;
}

type Props = BaseProps &
  React.DetailedHTMLProps<React.HTMLAttributes<HTMLWrapperType>, HTMLWrapperType> &
  React.SVGProps<SVGWrapperType>;

const noop = () => undefined;

const ReactSVG: FC<Props> = ({
  src,
  svgStyle,
  desc = "",
  title = "",
  wrapper = "div",
  loading: Loading = null,
  onError = noop,
  ...rest
}) => {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const reactWrapperRef = useRef<HTMLElement | null>(null);
  const nonReactWrapperRef = useRef<HTMLElement | null>(null);

  /**
   * Removes the currently injected SVG from the DOM.
   */
  const removeSVG = useCallback((): void => {
    if (nonReactWrapperRef.current?.parentNode) {
      nonReactWrapperRef.current.parentNode.removeChild(nonReactWrapperRef.current);
      nonReactWrapperRef.current = null;
    }
  }, []);

  /**
   * Injects the SVG into the DOM and handles any transformations, error handling, or callbacks.
   */
  const renderSVG = useCallback((): void => {
    if (reactWrapperRef.current instanceof ownerWindow(reactWrapperRef.current).Node) {
      const nonReactWrapper = document.createElement(wrapper === "svg" ? "svg" : wrapper);
      const nonReactTarget = document.createElement(wrapper === "svg" ? "svg" : wrapper);

      if (wrapper === "svg") {
        nonReactWrapper.setAttribute("xmlns", svgNamespace);
        nonReactWrapper.setAttribute("xmlns:xlink", xlinkNamespace);
        nonReactTarget.setAttribute("xmlns", svgNamespace);
        nonReactTarget.setAttribute("xmlns:xlink", xlinkNamespace);
      }

      nonReactWrapper.setAttribute("class", WrapperClassName);
      nonReactWrapper.appendChild(nonReactTarget);
      nonReactTarget.dataset.src = src;

      nonReactWrapperRef.current = reactWrapperRef.current?.appendChild(nonReactWrapper) || null;

      const handleError = (error: unknown): void => {
        removeSVG();
        setIsLoading(false);
        onError?.(error);
      };

      const afterEach = (error: Error | null, svg?: SVGSVGElement): void => {
        if (error) {
          handleError(error);
          return;
        }
        setIsLoading(false);
        try {
          if (svg) {
            if (svgStyle) {
              Object.assign(svg.style, svgStyle);
            }
          }
        } catch (injectionError) {
          handleError(injectionError);
        }
      };

      const beforeEach = (svg: SVGSVGElement): void => {
        svg.setAttribute("role", "img");

        if (desc) {
          const originalDesc = svg.querySelector(":scope > desc");
          if (originalDesc) {
            svg.removeChild(originalDesc);
          }
          const newDesc = document.createElement("desc");
          newDesc.textContent = desc;
          svg.prepend(newDesc);
        }

        if (title) {
          const originalTitle = svg.querySelector(":scope > title");
          if (originalTitle) {
            svg.removeChild(originalTitle);
          }
          const newTitle = document.createElement("title");
          newTitle.textContent = title;
          svg.prepend(newTitle);
        }

        try {
        } catch (error) {
          handleError(error);
        }
      };

      SVGInjector(nonReactTarget, { afterEach, beforeEach });
    }
  }, [desc, onError, removeSVG, src, svgStyle, title, wrapper]);

  /**
   * Effect to manage SVG injection on mount and clean up SVGs on unmount.
   */
  useEffect(() => {
    renderSVG();
    return () => {
      removeSVG();
    };
  }, [renderSVG, removeSVG]);

  const svgWrapperProps =
    wrapper === "svg" ? { xmlns: svgNamespace, xmlnsXlink: xlinkNamespace } : {};

  const elementProps = {
    ...rest,
    ...svgWrapperProps,
    ref: reactWrapperRef,
  };

  return createElement(wrapper, elementProps, isLoading && Loading && <Loading />);
};

export default ReactSVG;
